import { useEffect, useRef, useState } from "react";
import TeamLogo from "@/components/TeamLogo";
import { useLang } from "@/context/LangContext";
import { ChatCircleDotsIcon, SparkleIcon, UserIcon } from "@phosphor-icons/react";

const AWAY = { abbr: "NYY", logo: "https://www.mlbstatic.com/team-logos/147.svg", color: "#0C2340" };
const HOME = { abbr: "LAD", logo: "https://www.mlbstatic.com/team-logos/119.svg", color: "#005A9C" };

// Scripted example feed; each step adds one message and may update the scoreboard.
const SCRIPT = [
  { id: "s1", from: "tv", state: { away: 3, home: 2, wp: 41 },
    en: { text: "Bottom 7th · 2 outs · runner on 2nd" },
    es: { text: "Baja de la 7ª · 2 outs · corredor en segunda" } },
  { id: "s2", from: "gamly",
    en: { tag: "Why it matters", text: "Lefty-on-lefty here: the reliever holds LHB to a .198 average this year. The broadcast won't tell you the count favors the pitcher 70% of the time from 1-2." },
    es: { tag: "Por qué importa", text: "Zurdo contra zurdo: el relevista limita a los bateadores zurdos a .198 de promedio este año. La transmisión no te dirá que, con cuenta de 1-2, el pitcher gana el turno el 70% de las veces." } },
  { id: "s3", from: "fan",
    en: { text: "Why not pinch-hit right now?" },
    es: { text: "¿Por qué no meten un bateador emergente ahora?" } },
  { id: "s4", from: "gamly",
    en: { tag: "Explained", text: "The bench bat is right-handed but cold (2-for-24 last 10 days). The manager is betting matchup history over handedness." },
    es: { tag: "Explicado", text: "El emergente en la banca es derecho pero está frío (2 de 24 en los últimos 10 días). El mánager confía más en el historial del duelo que en la mano del bateador." } },
  { id: "s5", from: "tv", state: { away: 3, home: 3, wp: 56 },
    en: { text: "Line drive to right-center — run scores!" },
    es: { text: "¡Línea al jardín central derecho — anota la carrera!" } },
  { id: "s6", from: "gamly",
    en: { tag: "Win probability", text: "Tie game. Dodgers win odds jump 41% → 56% — the biggest swing of the night." },
    es: { tag: "Probabilidad de victoria", text: "Juego empatado. Las probabilidades de los Dodgers saltan de 41% a 56% — el cambio más grande de la noche." } },
];

const LABELS = {
  en: { sample: "Sample preview", wp: "Win probability", ask: "Ask Gamly anything about this game…" },
  es: { sample: "Vista de ejemplo", wp: "Probabilidad de victoria", ask: "Pregúntale a Gamly lo que quieras sobre este partido…" },
};

const STEP_MS = 2600;

function useScriptPlayer() {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => setCount((c) => (c >= SCRIPT.length ? 1 : c + 1)), count >= SCRIPT.length ? STEP_MS * 2 : STEP_MS);
    return () => clearTimeout(t);
  }, [count]);
  const shown = SCRIPT.slice(0, count);
  const state = shown.reduce((acc, s) => (s.state ? s.state : acc), SCRIPT[0].state);
  return { shown, state };
}

function Scoreboard({ state }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-white/10 bg-[#0b0f17]" data-testid="demo-scoreboard">
      {[AWAY, HOME].map((t, i) => (
        <div key={t.abbr} className={`flex items-center gap-3 ${i ? "flex-row-reverse" : ""}`}>
          <TeamLogo abbr={t.abbr} logo={t.logo} color={t.color} size={40} />
          <span className="font-num text-4xl font-black transition-all duration-500" data-testid={`demo-score-${t.abbr}`}>{i ? state.home : state.away}</span>
        </div>
      ))}
    </div>
  );
}

function WinBar({ wp, label }) {
  return (
    <div className="px-5 py-3 border-b border-white/10" data-testid="demo-win-prob">
      <div className="flex justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-white/45 mb-1.5">
        <span>NYY {100 - wp}%</span><span>{label}</span><span>LAD {wp}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden flex justify-end">
        <div className="h-full bg-[#0A84FF] transition-[width] duration-700 ease-out" style={{ width: `${wp}%` }} />
      </div>
    </div>
  );
}

function Message({ m, lang }) {
  const t = m[lang];
  if (m.from === "tv") {
    return <p className="fade-up text-center text-[11px] font-bold uppercase tracking-[0.18em] text-[#FDB927]">{t.text}</p>;
  }
  const fan = m.from === "fan";
  return (
    <div className={`fade-up flex gap-2.5 ${fan ? "flex-row-reverse" : ""}`}>
      <span className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${fan ? "bg-white/10" : "bg-[#0A84FF]"}`}>
        {fan ? <UserIcon size={14} weight="bold" /> : <SparkleIcon size={14} weight="fill" />}
      </span>
      <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${fan ? "bg-white/10 text-white" : "bg-[#121a28] border border-[#0A84FF]/25 text-white/85"}`}>
        {t.tag && <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#0A84FF] mb-1">{t.tag}</p>}
        {t.text}
      </div>
    </div>
  );
}

export default function CompanionDemo() {
  const { lang } = useLang();
  const L = LABELS[lang];
  const { shown, state } = useScriptPlayer();
  const feedRef = useRef(null);
  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
  }, [shown.length]);

  return (
    <div className="rounded-xl bg-[#080b12] border border-white/10 overflow-hidden shadow-[0_30px_80px_-30px_rgba(10,132,255,0.45)]" data-testid="companion-demo">
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-white/10 bg-[#0d1119]">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[#FF453A]">
          <span className="live-dot" /> Watch With Gamly
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/35" data-testid="demo-sample-label">{L.sample}</span>
      </div>
      <Scoreboard state={state} />
      <WinBar wp={state.wp} label={L.wp} />
      <div ref={feedRef} className="h-[320px] overflow-y-auto px-5 py-4 space-y-3.5" data-testid="demo-feed">
        {shown.map((m) => <Message key={m.id} m={m} lang={lang} />)}
      </div>
      <div className="flex items-center gap-2 px-5 py-3 border-t border-white/10 text-white/35 text-sm">
        <ChatCircleDotsIcon size={18} /> {L.ask}
      </div>
    </div>
  );
}
